import { useEffect } from 'react'
import useFeaturePermissions from '../../../hooks/useFeaturePermissions'
// Quote Context
import { useQuoteContext } from '../../../hooks/useQuoteContext'
import useCoTermMonths from '../../../hooks/useCoTermMonths'
// Bootstrap Imports
import { InputGroup, Form } from 'react-bootstrap'
import { PrepayLengthType } from '../../../types/types'

/**
 * PrepaySelect component - Dropdown for selecting prepayment length
 * NEW: Prepay length is decoupled from term length for flexible prepayment models
 * (e.g., 24-month term with 12-month prepay)
 */
export default function PrepaySelect() {
   const state = useQuoteContext()
   const { getString, prepayLengthsList } = state
   const { hasPermission } = useFeaturePermissions()
   const { coTermMonths } = useCoTermMonths()

   // If current prepayLength exceeds termLength, reset it to termLength or 1
   // Using useEffect to avoid setState during render
   useEffect(() => {
      if (state.termLength > 1 && state.prepayLength > state.termLength) {
         state.setPrepayLength(state.termLength)
      }
   }, [state.termLength, state.prepayLength, state.setPrepayLength])

   // Hides the component if user does not have roles and the prepay length is 1
   if (
      !hasPermission('InternalToolbarPrepaySelect', state.user_role) &&
      state.prepayLength === 1
   ) {
      return <></>
   }

   // Filter prepay options: if term > 1, only show prepay lengths <= term length
   // This ensures prepayment period doesn't exceed the contract term
   const filteredPrepayLengths = prepayLengthsList?.filter(
      (prepay: PrepayLengthType) => {
         // If term is month-to-month (1), show all prepay options
         if (state.termLength <= 1) return true
         // Otherwise, only show prepay lengths <= term length
         return prepay.prepay_length <= state.termLength
      },
   )

   // Dynamically inject a prepay option that matches the co-term length when a
   // contract is active. Mirrors the co-term synthetic-option pattern in
   // TermSelect.tsx (see CO_TERM_SENTINEL JSX option) — both selectors use the
   // same coTermMonths value from the shared useCoTermMonths hook so a co-term
   // quote can prepay through the end of the existing contract. The value is
   // also injected when the currently saved prepayLength simply isn't in the
   // role-filtered list (e.g. an arbitrary value previously saved).
   //
   // Unlike co-term on the term side, there is NO is_co_term-equivalent boolean
   // column for prepay; the raw prepay_length number on the opportunity is the
   // only signal, so the synthetic option just carries that number as both
   // value and label. (coTermMonths is sourced from the shared hook above.)
   const dynamicPrepayValue: number | null = (() => {
      const candidate =
         coTermMonths != null && coTermMonths > 0
            ? coTermMonths
            : state.prepayLength > 0
              ? state.prepayLength
              : null
      if (candidate == null) return null
      const alreadyInList = filteredPrepayLengths?.some(
         (p: PrepayLengthType) => p.prepay_length === candidate,
      )
      return alreadyInList ? null : candidate
   })()

   return (
      <InputGroup className='justify-content-center' size='sm'>
         <InputGroup.Text>
            {getString('product.PrepaySelect.InputText') || 'Prepay Length'}
         </InputGroup.Text>
         <Form.Select
            size='sm'
            value={state.prepayLength}
            onChange={(e) => state.setPrepayLength(Number(e.target.value))}
         >
            {filteredPrepayLengths &&
               filteredPrepayLengths
                  .sort(
                     (a: PrepayLengthType, b: PrepayLengthType) =>
                        a.display_order - b.display_order,
                  )
                  .map((prepay: PrepayLengthType) => (
                     <option key={prepay.id} value={prepay.prepay_length}>
                        {prepay.name}
                     </option>
                  ))}
            {dynamicPrepayValue != null && (
               <option key='dynamic-prepay' value={dynamicPrepayValue}>
                  {dynamicPrepayValue} Months
               </option>
            )}
         </Form.Select>
      </InputGroup>
   )
}
